import type { FlowchartNodeStyle, ParsedFlowchart } from './flowchart-parser-model';

const HASH_OFFSET_BASIS = 2166136261;
const HASH_PRIME = 16777619;

function serializeStyle(style: FlowchartNodeStyle | null): string {
  if (!style) {
    return '';
  }
  return [
    style.fill ?? '',
    style.stroke ?? '',
    style.color ?? '',
    style.strokeWidth ?? '',
  ].join(',');
}

function hashString(value: string): string {
  let hash = HASH_OFFSET_BASIS;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, HASH_PRIME);
  }
  return (hash >>> 0).toString(36);
}

export function buildFlowchartContentKey(parsed: ParsedFlowchart): string {
  const nodes = parsed.nodes
    .map((node) =>
      [
        node.id,
        node.label ?? '',
        node.labelOrigin,
        node.shape ?? '',
        node.parent ?? '',
        node.classNames.join(','),
        serializeStyle(node.inlineStyle),
      ].join(':'),
    )
    .sort();
  const subgraphs = parsed.subgraphs
    .map((subgraph) =>
      [
        subgraph.id,
        subgraph.label ?? '',
        subgraph.parent ?? '',
        subgraph.classNames.join(','),
        serializeStyle(subgraph.inlineStyle),
      ].join(':'),
    )
    .sort();
  const classDefs = Array.from(parsed.classDefs.entries())
    .map(([className, style]) => `${className}:${serializeStyle(style)}`)
    .sort();
  const edges = parsed.edges
    .map((edge) =>
      [
        edge.id,
        edge.edgeId ?? '',
        edge.from,
        edge.to,
        edge.label ?? '',
        edge.kind,
        edge.startMarker,
        edge.endMarker,
        edge.classNames.join(','),
      ].join(':'),
    )
    .sort();
  return hashString([parsed.direction, ...nodes, ...subgraphs, ...classDefs, ...edges].join('|'));
}
